import type { DirectoryEntry } from "@kontaktfix/shared";
import { Dialog, Input, Skeleton } from "@kontaktfix/ui";
import { CheckIcon, MagnifyingGlassIcon, UserIcon, UsersIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useDebounced } from "@/pages/contacts";

/** Search the customer's Microsoft 365 directory and pick a person or group. */
export function DirectoryPicker({
  open,
  onOpenChange,
  title,
  type = "all",
  excluded = [],
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  type?: "all" | "user" | "group";
  excluded?: string[];
  onPick: (entry: DirectoryEntry) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const q = useDebounced(query, 250);
  const [busy, setBusy] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["directory", type, q],
    queryFn: () => api.get<DirectoryEntry[]>(`/api/directory/search?type=${type}&q=${encodeURIComponent(q)}`),
    enabled: open,
  });

  const pick = async (entry: DirectoryEntry) => {
    setBusy(entry.id);
    try {
      await onPick(entry);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} className="max-w-lg">
      <Input
        icon={<MagnifyingGlassIcon />}
        autoFocus
        placeholder={type === "user" ? t("settings.members.search") : t("lists.assignments.search")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="mt-3 mb-4 flex max-h-80 flex-col gap-0.5 overflow-y-auto">
        {isLoading ? (
          <>
            <Skeleton className="h-11" />
            <Skeleton className="h-11" />
            <Skeleton className="h-11" />
          </>
        ) : !data?.length ? (
          <p className="py-6 text-center text-[13px] text-fg-muted">{t("lists.assignments.noResults")}</p>
        ) : (
          data.map((entry) => {
            const taken = excluded.includes(entry.id);
            return (
              <button
                key={`${entry.type}-${entry.id}`}
                type="button"
                disabled={taken || busy !== null}
                onClick={() => pick(entry)}
                className="flex items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-subtle text-fg-muted">
                  {entry.type === "group" ? (
                    <UsersIcon className="size-4" />
                  ) : (
                    <UserIcon className="size-4" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{entry.displayName}</span>
                  <span className="block truncate text-[12px] text-fg-muted">
                    {entry.type === "group" ? t("lists.assignments.group") : t("lists.assignments.user")}
                    {entry.detail ? ` · ${entry.detail}` : ""}
                  </span>
                </span>
                {taken && <CheckIcon className="size-4 text-success" />}
              </button>
            );
          })
        )}
      </div>
    </Dialog>
  );
}
